/// <amd-module name="VSS/Diagnostics/Dialog/PerfSettingsDialog" />
import * as React from "react";
import { IVssPerformanceDiagnosticsService, PerfSettingsProps } from "VSS/Diagnostics/PerfBar";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, VssComponent, css } from "VSS/Platform/Layout";
import { Dialog, IDialogProps } from "VSSUI/Dialog";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Toggle } from "VSSUI/Toggle";

interface PerfSettingsState {
    breakOnFirstRender?: boolean;
    breakOnTti?: boolean;
    bundlingEnabled?: boolean;
    cdnAvailable?: boolean;
    cdnEnabled?: boolean;
    debugEnabled?: boolean;
    minificationEnabled?: boolean;
    reactStrictModeEnabled?: boolean;
    diagnosticsEnabled?: boolean;
    esNextSupported?: boolean;
    scriptType?: string;
    tracePointCollectorEnabled?: boolean;
}

class PerfSettingsConfiguration extends VssComponent<{}, PerfSettingsState> {
    private performanceDiagnosticsService: IVssPerformanceDiagnosticsService;
    private disableDebugAssetsFFEnabled: boolean;

    public constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.performanceDiagnosticsService = context.pageContext.getService<IVssPerformanceDiagnosticsService>("IVssPerformanceDiagnosticsService");
        this.disableDebugAssetsFFEnabled = isFeatureFlagEnabled(context.pageContext, "VisualStudio.Services.ExtensionManagement.DoNotLoadDebugAssets", false);
        this.state = {
            breakOnFirstRender: this.performanceDiagnosticsService.isBreakOnFirstRenderEnabled(),
            breakOnTti: this.performanceDiagnosticsService.isBreakOnTtiEnabled(),
            bundlingEnabled: this.performanceDiagnosticsService.isBundlingEnabled(),
            cdnAvailable: this.performanceDiagnosticsService.isCdnAvailable(),
            cdnEnabled: this.performanceDiagnosticsService.isCdnEnabled(),
            debugEnabled: this.performanceDiagnosticsService.isDebugEnabled(),
            minificationEnabled: !this.disableDebugAssetsFFEnabled && !this.performanceDiagnosticsService.isDebugEnabled(),
            reactStrictModeEnabled: this.performanceDiagnosticsService.isReactStrictModeEnabled(),
            diagnosticsEnabled: this.performanceDiagnosticsService.isDiagnosticsEnabled(),
            scriptType: this.performanceDiagnosticsService.getScriptType(),
            tracePointCollectorEnabled: this.performanceDiagnosticsService.isTracePointCollectionEnabled()
        };
    }

    public render(): JSX.Element {
        let minificationLabel = "Minification";
        if (!this.state.bundlingEnabled) {
            minificationLabel += " (must enable Bundling)";
        }

        let cdnLabel = "CDN";
        if (!this.state.cdnAvailable) {
            cdnLabel += " (not configured)";
        } else if (!this.state.bundlingEnabled) {
            cdnLabel += " (must enable Bundling)";
        }

        let breakOnTtiLabel = "Break On TTI";
        let breakOnFirstRenderLabel = "Break On First Render";
        let reactStrictModeLabel = "React Strict Mode";
        if (!this.state.debugEnabled) {
            const suffix = " (must disable Minification)";
            breakOnTtiLabel += suffix;
            breakOnFirstRenderLabel += suffix;
            reactStrictModeLabel += suffix;
        }

        const isEsNextSupported = this.performanceDiagnosticsService.isEsNextSupported();
        let scriptTypeOnText = "ES2017";
        let scriptTypeOfftext = "ES5";
        if (!this.state.bundlingEnabled) {
            scriptTypeOnText = "ES2017 (must enable Bundling)";
        } else if (!isEsNextSupported) {
            scriptTypeOfftext = "ES5 (unsupported browser)";
        }

        let bundlingLabel = "Bundling";
        if (!isEsNextSupported) {
            bundlingLabel = "Bundling (unsupported browser)";
        }

        return (
            <div className="flex-column">
                {this.disableDebugAssetsFFEnabled && 
                    <MessageCard
                        className="flex-self-stretch no-h-padding"
                        severity={MessageCardSeverity.Warning}
                    >
                        {"\"VisualStudio.Services.ExtensionManagement.DoNotLoadDebugAssets\" feature flag is enabled.\n\r"
                        + "Changes to \"Bundling\" setting will not save unless it is turned off and server restarted (iisreset)."}
                    </MessageCard>
                }
                <Toggle
                    className="perf-setting-toggle"
                    checked={this.state.bundlingEnabled}
                    text={bundlingLabel}
                    onChange={this.setBundlingCookie}
                    disabled={!isEsNextSupported}
                />
                <Toggle
                    className="perf-setting-toggle"
                    checked={this.state.bundlingEnabled && this.state.minificationEnabled}
                    text={minificationLabel}
                    disabled={!this.state.bundlingEnabled || this.disableDebugAssetsFFEnabled}
                    onChange={this.setMinification}
                />
                <Toggle
                    className="perf-setting-toggle"
                    checked={this.state.scriptType !== "javascript"}
                    offText={scriptTypeOfftext}
                    onText={scriptTypeOnText}
                    text="ESNext scripts"
                    disabled={!this.state.bundlingEnabled || !isEsNextSupported}
                    onChange={this.setScriptTypeCookie}
                />
                <Toggle
                    className="perf-setting-toggle"
                    checked={this.state.diagnosticsEnabled}
                    text="Diagnostics"
                    onChange={this.setDiagnosticsCookie}
                />
                <Toggle
                    className="perf-setting-toggle"
                    checked={this.state.tracePointCollectorEnabled}
                    text="Perf Bar"
                    onChange={this.setTracePointCookie}
                />
                <Toggle
                    className="perf-setting-toggle"
                    checked={this.state.cdnAvailable && this.state.cdnEnabled}
                    text={cdnLabel}
                    disabled={!this.state.cdnAvailable || !this.state.bundlingEnabled}
                    onChange={this.setCdnCookie}
                />
                <Toggle
                    className="perf-setting-toggle"
                    checked={this.state.breakOnTti && this.state.debugEnabled}
                    text={breakOnTtiLabel}
                    disabled={!this.state.debugEnabled}
                    onChange={this.setBreakOnTtiCookie}
                />
                <Toggle
                    className="perf-setting-toggle"
                    checked={this.state.breakOnFirstRender && this.state.debugEnabled}
                    text={breakOnFirstRenderLabel}
                    disabled={!this.state.debugEnabled}
                    onChange={this.setBreakOnFirstRenderCookie}
                />
                <Toggle
                    className="perf-setting-toggle"
                    checked={this.state.reactStrictModeEnabled && this.state.debugEnabled}
                    text={reactStrictModeLabel}
                    disabled={!this.state.debugEnabled}
                    onChange={this.setReactStrictModeCookie}
                />
            </div>
        );
    }

    private setBundlingCookie = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, state: boolean) => {
        this.performanceDiagnosticsService.setBundlingEnabled(state);
        this.setState({
            bundlingEnabled: state
        });

        // unbundled ES5 scripts are not built
        if (!state) {
            this.setState({
                scriptType: "ES2017"
            });
        } else {
            this.setState({
                scriptType: this.performanceDiagnosticsService.getScriptType()
            });
        }
    };

    private setMinification = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, state: boolean) => {
        this.performanceDiagnosticsService.setDebugEnabled(!state);
        this.setState({
            debugEnabled: !state,
            minificationEnabled: state
        });
    };

    private setDiagnosticsCookie = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, state: boolean) => {
        this.performanceDiagnosticsService.setDiagnosticsEnabled(state);
        this.setState({
            diagnosticsEnabled: state
        });
    };

    private setScriptTypeCookie = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, state: boolean) => {
        const scriptTypeValue = state ? "ES2017" : "javascript";
        this.performanceDiagnosticsService.setScriptType(scriptTypeValue);
        this.setState({
            scriptType: scriptTypeValue
        });
    };

    private setTracePointCookie = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, state: boolean) => {
        this.performanceDiagnosticsService.setTracePointEnabled(state);
        this.setState({
            tracePointCollectorEnabled: state
        });
    };

    private setCdnCookie = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, state: boolean) => {
        this.performanceDiagnosticsService.setCdnEnabled(state);
        this.setState({
            cdnEnabled: state
        });
    };

    private setBreakOnTtiCookie = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, state: boolean) => {
        this.performanceDiagnosticsService.setBreakTtiEnabled(state);
        this.setState({
            breakOnTti: state
        });
    };

    private setBreakOnFirstRenderCookie = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, state: boolean) => {
        this.performanceDiagnosticsService.setBreakOnFirstRenderEnabled(state);
        this.setState({
            breakOnFirstRender: state
        });
    };

    private setReactStrictModeCookie = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, state: boolean) => {
        this.performanceDiagnosticsService.setReactStrictModeEnabled(state);
        this.setState({
            reactStrictModeEnabled: state
        });
    };
}

class PerfSettingsDialog extends VssComponent<PerfSettingsProps, PerfSettingsState> {
    public render(): JSX.Element {
        const dialogProps: IDialogProps = {
            calloutClassName: "dialog-container",
            calloutContentClassName: "performance-dialog",
            lightDismiss: false,
            onDismiss: this.props.onDismiss,
            showCloseButton: true,
            titleProps: { text: "Diagnostic Settings" }
        };

        return (
            <Dialog {...dialogProps}>
                <PerfSettingsConfiguration />
            </Dialog>
        );
    }
}

Components.add("vss.perf-settings-dialog", PerfSettingsDialog);

class PerfSettingsConfigurationPage extends VssComponent {
    public render() {
        return (
            <div className="perf-settings-configuration-page">
                <h2>Diagnostics Settings</h2>
                <PerfSettingsConfiguration />
            </div>
        );
    }
}

Components.add("vss.perf-settings-configuration-page", PerfSettingsConfigurationPage);
